import {
    SimplePresentationRunner,
    SimplePresentationRunnerController
} from "../../core/winpresentation/SimplePresentationRunner";
import {gsap, TimelineMax} from "gsap";
import {Game} from "../Game";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {Config} from "../config/Config";
import {SimpleReelModelEventData, SimpleReelModelState} from "../../core/reel/simplereel/SimpleReelModel";
import {Sound} from "../sound/Sound";
import {ArrayUtils} from "../utils/ArrayUtils";
import {SymbolContainer} from "../reels/SymbolContainer";
import {GameUtils} from "../utils/GameUtils";
import {GameFeatureMode, Mental2SymbolStates, SYMBOL} from "../enums/Enum";
import {IStickyScatter} from "../server/Data";
import {XMentalSymbol} from "../reels/XMentalSymbol";

export class ReelStopRunner implements SimplePresentationRunner {
    private _tl: TimelineMax | undefined;
    private _stoppedReels: boolean[];
    private _stoppedReelsWithFireFrames: boolean[];
    private _isSkipped: boolean;
    private _deadSymLandingSoundCounter: number;
    private _s1LandingSoundCounter: number;
    private _s2LandingSoundCounter: number;

    shouldRun(): boolean {
        return true;
    }

    start(flowStateController: SimplePresentationRunnerController): void {
        Game.anticipationView.reset();
        this._deadSymLandingSoundCounter = 0;
        this._s1LandingSoundCounter = 0;
        this._s2LandingSoundCounter = 0;
        this._stoppedReels = [false, false, false, false, false];
        this._stoppedReelsWithFireFrames = [false, false, false, false, false];
        this._isSkipped = false;
        Game.reelView.resetDeadSymArr();
        this._tl = new TimelineMax();
        this._tl.add(() => {
            flowStateController.setSkipCallback(() => this.skip());
        });

        const isFastSpin: boolean = Game.apiPlugin.settings.get(APISetting.FAST_SPIN, false);
        let stopDelay: number = 0,
            stopTime: number = 0;

        if (isFastSpin) {
            this._tl.timeScale(1.5);
        }
        const isxGodSpin: boolean = Game.model.xGodInReels[0] > -1;

        for (let reelId: number = 0; reelId < Game.model.gameData.reels.length; reelId++) {

            const reelStopAnimation: TimelineMax = new TimelineMax();
            const nextReelId: number = reelId + 1;
            if (isxGodSpin) {
                if (nextReelId < 5 && Game.model.xGodInReels[reelId] > -1) {
                    stopDelay = isFastSpin ? Config.X_GOD_DELAY_FAST : Config.X_GOD_DELAY_NORMAL;
                    if (nextReelId === 3) {
                        stopDelay += 1; //#381
                    }
                    if (nextReelId === 4) {
                        stopDelay += Math.floor(Math.random() * 3 + 4);
                    }
                } else {
                    stopDelay = Config.X_GOD_DELAY_FAST;
                }
            } else if (nextReelId < 5 && Game.model.scatterAnticipationReels[nextReelId]) {
                stopDelay = isFastSpin ? Config.SCATTER_ANTICIPATION_DELAY_FAST : Config.SCATTER_ANTICIPATION_DELAY_NORMAL;
                if (nextReelId === 4 && !Game.model.gameData.wasFeatureBuy && Game.model.isEligibleForSFSTriggered()) {
                    stopDelay += isFastSpin ? 2.5 : 3; //#438 extra 3 sec
                }
            } else if (nextReelId < 5 && Game.model.shouldPlayXMentalAnticipation(nextReelId)) {
                stopDelay = isFastSpin ? Config.X_MENTAL_DELAY_FAST : Config.X_MENTAL_DELAY_NORMAL;
            } else if (reelId === 0 && Game.model.shouldPlayXMentalAnticipation(0)) { //reel id 0 added for xMental anticipation
                stopTime = isFastSpin ? Config.X_MENTAL_DELAY_FAST : Config.X_MENTAL_DELAY_NORMAL;
                this._tl.add(() => {
                    Game.soundPlayer.playSound(Sound.X_MENTAL_ANTICIPATION);
                }, 0);
            } else if (reelId === 1 && Game.model.shouldPlayXHoleAnticipation()) {
                stopDelay = isFastSpin ? Config.XHOLE_ANTICIPATION_DELAY_FAST : Config.XHOLE_ANTICIPATION_DELAY_NORMAL;
            } else {
                stopDelay = isFastSpin ? Config.REEL_STOP_DELAY_FAST_SPIN : Config.REEL_STOP_DELAY;
            }


            reelStopAnimation.add(this.preventSkipOnLastReel(reelId, flowStateController));
            reelStopAnimation.add(Game.reelView.reelModels[reelId].stop(Game.model.gameData.reels[reelId]));

            Game.reelView.reelModels[reelId].events?.once(SimpleReelModelState.STOPPED, (data: SimpleReelModelEventData) => this.onReelStop(data, flowStateController));
            Game.reelView.reelModels[reelId].events?.once(SimpleReelModelState.BOUNCE, (data: SimpleReelModelEventData) => this.onReelBounce(data));

            this._tl.add(reelStopAnimation, stopTime);
            stopTime += stopDelay;
        }


        const padding = Game.gameFlowController.calculatePaddingDuration(this._tl.totalDuration());
        if (padding > 0) {
            //Logger.logDev("Added minimum jurisdiction padding  ", padding);
            //Logger.logDev("Total before padding:"+this._tl.totalDuration());
            this._tl.shiftChildren(padding);
            //Logger.logDev("Total after padding:"+this._tl.totalDuration());
        }
    }

    private preventSkipOnLastReel(reelId: number, flowStateController: SimplePresentationRunnerController) {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (reelId == 4) {
            tl.add(() => {
                this._isSkipped = false;
                flowStateController.setSkipCallback();
            }); //prevents useless skips when last reel has started its stop anyway.
        }
        return tl;
    }

    private skip(): void {
        this._tl?.kill();
        this._isSkipped = true;
        this._tl = new TimelineMax();
        const stopDelay = Config.STEP_DURATION;
        let stopTime = 0;
        this._tl.add(() => {
            this.removeDarkFromSymbols();
        });
        for (let i: number = 0; i < Game.model.gameData.reels.length; i++) {
            const reelModel = Game.reelView.reelModels[i];
            if (reelModel.state != SimpleReelModelState.SPIN) {
                continue;
            }

            const reelData = Game.model.gameData.reels[i];
            const reelStopAnimation = new TimelineMax();
            //reelStopAnimation.add(() => Game.textView.update(`Reel ${i} quick stopping, [${reelData.toString()}]`));
            reelStopAnimation.add(Game.reelView.reelModels[i].stop(reelData));
            this._tl.add(reelStopAnimation, stopTime);
            stopTime += stopDelay;
        }
    }

    private onReelBounce(data: SimpleReelModelEventData) {
        Game.anticipationView.handleXGodStuffs(data.reel);
        if (data.reel === 4) {
            this.activateEnhancer();
        }
        let isAnyScatters: boolean = false;
        if (Game.model.gameData.reels[data.reel].includes(SYMBOL.S1)) {
            isAnyScatters = true;
            if (this._s1LandingSoundCounter === 2) {
                GameUtils.symbolLandShake(0, 100, 0.1);
            } else {
                GameUtils.symbolLandShake(0, 10 * (this._s1LandingSoundCounter + 1), 0.06);
            }
        }
        if (Game.model.gameData.reels[data.reel].includes(SYMBOL.S2)) {
            isAnyScatters = true;
            if (this._s2LandingSoundCounter === 1) {
                GameUtils.symbolLandShake(0, 100, 0.1);
            } else {
                GameUtils.symbolLandShake(0, 10 * (this._s2LandingSoundCounter + 2), 0.06)
            }
        }
        if (Game.model.gameData.reels[data.reel].includes(SYMBOL.DEAD) && !isAnyScatters) {
            if (this._deadSymLandingSoundCounter === 1) {
                GameUtils.symbolLandShake(0, 60, 0.1);
            } else {
                GameUtils.symbolLandShake(0, 10 * (this._deadSymLandingSoundCounter + 2), 0.08)
            }
        }

        if (Game.model.gameData.reels[data.reel].includes(SYMBOL.X_MENTAL) && Game.model.isXMentalActive()) {
            GameUtils.symbolLandShake(0, 60, 0.1);
        }
    }

    private handleFireFramesAction(reelId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        let isSplitSoundPlayed: boolean = false;
        Game.model.gameData.fireFrames[reelId].forEach((isFireFrame: boolean, rowId: number) => {
            const isDeadSym: boolean = Game.model.gameData.evaluatedArea[reelId][rowId] === SYMBOL.DEAD;

            let size: number = Game.model.gameData.fireReels[reelId] ? 3 : 2;
            if ((Game.model.gameData.fireReels[reelId] || isFireFrame) && rowId < Config.SYMBOLS_IN_REEL[reelId]) {
                if (isDeadSym) {
                    Game.model.deadSymCount += size;
                }
                const lockSize: number = Game.model.gameData.lockedFrameBefore[reelId][rowId];
                const symbolContainer: SymbolContainer = Game.reelView.getVisibleSymbolAt(reelId, rowId);
                if (symbolContainer) {
                    const parts: number = size;
                    let revealSym: string;
                    if (Game.model.stickyS2[reelId] === rowId) {
                        revealSym = Game.model.gameData.evaluatedArea[reelId][rowId]
                    } else {
                        revealSym = Game.model.gameData.reels[reelId][rowId];
                    }

                    tl.add([
                        Game.fireFrameView.stop(reelId, rowId, parts, lockSize),
                        () => {
                            if (!isSplitSoundPlayed) {
                                Game.soundPlayer.playFireFrameSplit();
                                isSplitSoundPlayed = true;
                            }
                        }
                    ], 0);
                    if (Game.model.gameData.reels[reelId][rowId] !== SYMBOL.X_HOLE && Game.model.gameData.reels[reelId][rowId] !== SYMBOL.X_MENTAL) {
                        tl.add(symbolContainer.split(parts, revealSym), 0);
                    }
                }
            } else {
                if (rowId < Config.SYMBOLS_IN_REEL[reelId] && isDeadSym) {
                    Game.reelView.addDeadSymInfo(reelId + "_" + rowId);
                    Game.model.deadSymCount++;
                }
            }
        });
        return tl;
    }

    private handleBonusStuffs(reelId: number): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        Game.model.gameData.lockedS2inSFS.forEach((stickyScatterData: IStickyScatter) => {
            if (stickyScatterData.x === reelId) {
                const symbolContainer: SymbolContainer = <SymbolContainer>Game.reelView.reelModels[stickyScatterData.x].getStopSymbolAt(stickyScatterData.y).positionable;
                symbolContainer.changeSymbol(0, stickyScatterData.sym).progress(1);
                tl.add(Game.stickyView.hideScatters(stickyScatterData.x, stickyScatterData.y));
            }
        });
        return tl;
    }

    private playReelStopSpinSound() {
        if (ArrayUtils.isAllTrue(this._stoppedReels)) {
            Game.soundPlayer.stopSound(Sound.REELS_SPINNING);
            if (Game.model.isXGodMaxWinTriggered()) {
                Game.soundPlayer.stopSound(Game.anticipationView.xGodReelSpinPreviousSound);
            }
            Game.soundPlayer.stopSound(Sound.X_GOD_FINAL_REEL_ANTICIPATION);
        }
    }

    private playLanding(reelId: number,): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (reelId === 2) {
            tl.add(() => {
                Game.effectsView.playXHoleLandAnimation();
            });
        }
        let anyScatters: boolean = false;
        let s1SoundArr: string[] = Sound.S1_LAND_DEAD; //#434
        if (Game.model.gameData.reels[0].includes(SYMBOL.S1) && Game.model.gameData.reels[2].includes(SYMBOL.S1)) {
            s1SoundArr = Sound.S1_LAND;
        }
        Game.model.data.reels[reelId].forEach((sym: string, rowId: number) => {
            switch (sym as SYMBOL) {
                case SYMBOL.S1 :
                    Game.soundPlayer.playSound(s1SoundArr[this._s1LandingSoundCounter]);
                    this._s1LandingSoundCounter++;
                    anyScatters = true;
                    break;
                case SYMBOL.S2 :
                    anyScatters = true;
                    this._s2LandingSoundCounter++;
                    Game.soundPlayer.playSound(Sound.SCATTER2_LAND);

                    break;
                case SYMBOL.X_MENTAL :
                    const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelId, rowId);
                    Game.soundPlayer.stopSound(Sound.X_MENTAL_ANTICIPATION);
                    (symbolContainer.symbols[0] as XMentalSymbol).playLanding(symbolContainer.isTopHalf).add(() => {
                        if (Game.model.isXMentalActive()) {
                            Game.foregroundElements.featureLogoContainer.playXMentalActive();
                            (symbolContainer.symbols[0] as XMentalSymbol).playAnticipation();
                            Game.soundPlayer.playSound(Sound.X_MENTAL_LANDING);
                        } else {
                            if (Game.model.shouldPlayXMentalAnticipation(reelId)) {
                                Game.soundPlayer.playSound(Sound.X_MENTAL_END);
                            }
                            (symbolContainer.symbols[0] as XMentalSymbol).playMiss(); //#400
                        }
                    });
                    break;
                case SYMBOL.DEAD :
                    this._deadSymLandingSoundCounter++;
                    if (this._deadSymLandingSoundCounter === 1 || this._deadSymLandingSoundCounter === 2) {
                        Game.soundPlayer.playSound(Sound.DEAD_LAND + this._deadSymLandingSoundCounter);
                    }
                    break;
            }
        });

        if (reelId === 3 || reelId === 4) {
            if (this._s1LandingSoundCounter === 2 && !anyScatters) {
                Game.soundPlayer.playSound(Sound.S1_ANTICIPATION_REEL_STOP);
            }
        }

        return tl;
    }

    private onReelStop(data: SimpleReelModelEventData, flowStateController: SimplePresentationRunnerController) {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        Game.anticipationView.updateScatterCount(data.reel);
        if (Game.model.shouldPlayAnticipation()) {
            Game.anticipationView.scatterReelHighlightAnticipation(data.reel, this._isSkipped);
        }
        const nextReelId = data.reel + 1;
        if (nextReelId < 5 && Game.model.shouldPlayXMentalAnticipation(nextReelId)) {
            Game.soundPlayer.playSound(Sound.X_MENTAL_ANTICIPATION);
        }
        this._stoppedReels[data.reel] = true;

        Game.soundPlayer.playSound(Sound.REELS_LANDING[data.reel]);
        Game.anticipationView.handleXGodSounds(data.reel);
        tl.add(() => this.playReelStopSpinSound());

        tl.add(this.playLanding(data.reel));

        if (Game.model.gameData.mode !== GameFeatureMode.NORMAL) {
            tl.add(this.handleBonusStuffs(data.reel));
        }

        tl.add(this.handleFireFramesAction(data.reel));
        tl.add(() => {
            if (!this._isSkipped) {
                this.dimDownSymbols(data.reel);
            }
        });

        if (Game.model.deadSymCount > 1) {
            tl.add(Game.reelView.doActivateDeadSymbols(), 0);
        }

        if (Game.model.shouldPlayAnticipation()) {
            tl.add(Game.anticipationView.playScatterSymbolAnticipation(data.reel));
        }

        if (Game.model.isStickyDeadApplicable()) {
            tl.add(() => {
                Game.stickyView.distributeDeadMultiplierToReel(data.reel);
            });
        }

        if (ArrayUtils.isAllTrue(this._stoppedReels)) {
            Game.reelView.reelModels[2].STEP_DURATION = Config.STEP_DURATION;
            //stopping scatter landing animation
            for (let reelID: number = 0; reelID < 5; reelID++) {
                if (Game.model.scatterLandedPos[reelID] > -1) {
                    const symbolContainer: SymbolContainer = GameUtils.getSymbol(reelID, Game.model.scatterLandedPos[reelID]);
                    tl.add(() => {
                        symbolContainer.symbols.forEach((symbol) => {
                            let animName: string = symbol.state.tracks[0].animation.name;
                            if (animName.includes("_anticipation")) {
                                animName = animName.replace("_anticipation", "");
                            } else {
                                animName = animName.split("_")[2];
                            }
                            symbol.setState(GameUtils.getReSizeState(Number(animName)));
                        })
                    })
                }
            }
            tl.add(() => {
                if (this._s1LandingSoundCounter === 2 && !Game.model.data.reels[data.reel].includes(SYMBOL.S1)) {
                    Game.soundPlayer.playSound(Sound.S1_ANTICIPATION_END);
                }
                Game.soundPlayer.stopSound(Sound.S1_ANTICIPATION);

                Game.effectsView.stopScatterAnticipationEffect();
                this.removeDarkFromSymbols();
                Game.anticipationView.stopBgAnticipationEffects();
                Game.anticipationView.offXGodModeStuffs(-1);
            });
            if (Game.model.data.mode === GameFeatureMode.NORMAL && Game.model.data.nextMode !== GameFeatureMode.NORMAL && Game.model.data.nextMode !== GameFeatureMode.RESPIN) { //#418
                tl.add([Game.effectsView.playAndHideFlashEffect(SYMBOL.S1),
                    Game.effectsView.playAndHidePlusTextAnimation()
                ]);
                tl.to({}, {duration: 0.4}); //delay #178
            }
        }
        tl.add(() => {
            this._stoppedReelsWithFireFrames[data.reel] = true;
            if (ArrayUtils.isAllTrue(this._stoppedReelsWithFireFrames)) {
                this.completePresentation(flowStateController);
            }
        });
    }

    private dimDownSymbols(reelId: number): void {
        if (this._s1LandingSoundCounter === 2 && (Game.model.gameData.reels[0].includes(SYMBOL.S1) && Game.model.gameData.reels[2].includes(SYMBOL.S1))) {
            let anticipationType: Mental2SymbolStates = Mental2SymbolStates.STANDARD_FS_ANTICIPATION;
            if (this._s2LandingSoundCounter === 1) {
                anticipationType = Mental2SymbolStates.COOL_FS_ANTICIPATION;
            } else if (this._s2LandingSoundCounter === 2) {
                anticipationType = Mental2SymbolStates.SUPER_FS_ANTICIPATION;
            }
            if (reelId === 2) {
                for (let toBeDarkenReelId: number = 0; toBeDarkenReelId < 3; toBeDarkenReelId++) {
                    Game.model.gameData.reels[toBeDarkenReelId].forEach((symName: string, rowId: number) => {
                        if (symName != SYMBOL.S1 && symName != SYMBOL.S2) {
                            const symContainer: SymbolContainer = GameUtils.getSymbol(toBeDarkenReelId, rowId);
                            symContainer.setState(anticipationType);
                        }
                    });
                }
            } else if (reelId === 3) {
                for (let toBeDarkenReelId: number = 0; toBeDarkenReelId < 4; toBeDarkenReelId++) {
                    Game.model.gameData.reels[toBeDarkenReelId].forEach((symName: string, rowId: number) => {
                        if (symName != SYMBOL.S1 && symName != SYMBOL.S2) {
                            const symContainer: SymbolContainer = GameUtils.getSymbol(toBeDarkenReelId, rowId);
                            symContainer.setState(anticipationType);
                        }
                    });
                }
            }
        }
    }

    private removeDarkFromSymbols(): void {
        for (let reelId: number = 0; reelId < 5; reelId++) {
            Game.model.gameData.reels[reelId].forEach((symName: string, rowId: number) => {
                if (symName != SYMBOL.S1 && symName != SYMBOL.S2) {
                    const symContainer: SymbolContainer = GameUtils.getSymbol(reelId, rowId);
                    symContainer.setState(Mental2SymbolStates.NORMAL);
                }
            });
        }
    }

    private activateEnhancer(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = new gsap.core.Timeline();
        if (Game.model.fireFrameCount >= 4) {
            tl.add(Game.enhancerView.playFireOnEnhancer(0), 0);
        }
        if (Game.model.fireFrameCount >= 6) {
            tl.add(Game.enhancerView.playFireOnEnhancer(2), 0);
        }
        if (Game.model.fireFrameCount >= 8) {
            tl.add(Game.enhancerView.playFireOnEnhancer(1), 0);
        }
        return tl;
    }

    private completePresentation(flowStateController: SimplePresentationRunnerController) {
        Config.IS_SCATTER_ANTICIPATION_REEL_ACTIVE = [false, false, false, false, false];
        this._isSkipped = false;
        flowStateController.finish();
        delete this._tl;
    }
}